import { FinanceRate, PayoutData } from '@kanda-libs/ks-frontend-services';

import { formatDateForDisplay } from 'common/helpers';
import { HEADER } from 'components/job/JobsListPage/JobListPageActions/DownloadCsvButton/constants';
import { stringify } from 'csv-stringify/sync';

export type FinanceOptionDetails = {
  interest: number;
  duration: number;
};

/**
 * Converts finance option name into interest and duration
 * @param {String} financeOption finance option name, e.g. INTEREST_BEARING_60_1390
 *
 * @returns {FinanceOptions} Finance options object
 */
export const getFinanceOptionDetails = (
  financeOption?: FinanceRate,
): FinanceOptionDetails => {
  if (!financeOption) return { interest: 0, duration: 0 };

  const { name } = financeOption;
  const splitName = name?.split('_');
  if (name.toLowerCase().includes('free'))
    return {
      interest: 0,
      duration: parseInt(splitName.slice(-1)[0], 10),
    };
  const [durationString, interestString] = splitName.slice(-2);
  return {
    interest: parseInt(interestString, 10) / 100,
    duration: parseInt(durationString, 10),
  };
};

export const encodeData = (data: string[][]): string => {
  const [header, ...content] = data;
  return stringify(content, {
    columns: header,
    header: true,
    cast: {
      boolean: (v) => (v ? 'Yes' : 'No'),
    },
  });
};

export const includeZero = (value: number): string => `0${value}`.slice(-2);

export const getFileName = () => {
  const date = new Date();
  const formatted = `${date.getFullYear()}-${includeZero(
    date.getMonth() + 1,
  )}-${includeZero(date.getDate())}`;
  return `payouts-${formatted}.csv`;
};

export const formatPayoutData = (data: PayoutData[]) => {
  const formatted = data.map((datum: PayoutData) => {
    const rowValue = {
      reference: datum.credit_id,
      price: (datum.price / 100).toFixed(2).toString(),
      deposit: (datum.selected_deposit / 100).toFixed(2).toString(),
      tpDeposit: datum.tp_own_deposit ? 'Yes' : 'No',
      rate: (datum.rate / 100).toString(),
      term: datum.term.toString(),
      customerName: datum.customer_name,
      customerEmail: datum.customer_email,
      jobTitle: datum.job_title,
      tradespersonName: datum.tp_name,
      fee1: (datum.kanda_fee / 100).toFixed(2).toString(),
      fee2: (datum.kanda_charge / 100).toFixed(2).toString(),
      amountToPay: (datum.amount_to_pay / 100).toFixed(2).toString(),
      status: datum.check_status,
      date: formatDateForDisplay(new Date(datum.job_updated_at)),
      tradespersonEmail: datum.tp_email,
      bankName: datum.bank_account_name,
      bankNum: datum.bank_account_number,
      sortCode: datum.bank_account_sortcode,
      dateLoanCreated: formatDateForDisplay(datum.credit_created_at),
      legacyRate: datum.legacy ? 'true' : 'false',
    };

    return rowValue;
  });

  return [HEADER].concat(formatted.map((row) => Object.values(row)));
};
